import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFailureDetector } from './apiFailureDetection';
import CacheManagerUtility from './cacheManagerUtility';
import { saveToCache, getCacheEntry } from './localStorageCache';

export interface FallbackDataOptions {
  maxAge?: number;
  priority?: 'high' | 'medium' | 'low';
  fallbackValue?: any;
  showStaleIndicator?: boolean;
}

export interface FallbackResult<T> {
  data: T | null;
  isFromCache: boolean;
  cacheAge: number;
  isStale: boolean;
  lastUpdated: number;
  source: 'api' | 'cache' | 'fallback';
}

export class FallbackDataService {
  private queryClient: QueryClient;
  private cacheManager: CacheManagerUtility;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
    this.cacheManager = new CacheManagerUtility();
  }

  public async getDataWithFallback<T>(
    queryKey: string[],
    fetcher: () => Promise<T>,
    options: FallbackDataOptions = {}
  ): Promise<FallbackResult<T>> {
    const {
      maxAge = 30 * 60 * 1000, // 30 minutes default
      priority = 'medium',
      fallbackValue = null,
      showStaleIndicator = true,
    } = options;

    const endpoint = this.extractEndpointFromQueryKey(queryKey);
    const shouldUseFallback = apiFailureDetector.shouldUseFallback(endpoint);
    const cacheKey = queryKey.join(':');

    // Try to get fresh data first if not in fallback mode
    if (!shouldUseFallback) {
      try {
        const freshData = await fetcher();
        apiFailureDetector.recordSuccess(endpoint);
        
        // Cache the fresh data
        this.cacheManager.set(cacheKey, freshData, {
          priority,
          maxAge,
        });
        saveToCache(queryKey, freshData);

        return {
          data: freshData,
          isFromCache: false,
          cacheAge: 0,
          isStale: false,
          lastUpdated: Date.now(),
          source: 'api',
        };
      } catch (error) {
        console.warn(`API call failed for ${endpoint}, falling back to cache:`, error);
        apiFailureDetector.recordFailure(endpoint, error as Error);
        // Continue to fallback logic below
      }
    }

    // Try to get cached data
    const cachedResult = this.cacheManager.get(cacheKey);
    if (cachedResult) {
      const cacheAge = Date.now() - cachedResult.timestamp;
      const isStale = cacheAge > maxAge;

      return {
        data: cachedResult.data as T,
        isFromCache: true,
        cacheAge,
        isStale,
        lastUpdated: cachedResult.timestamp,
        source: 'cache',
      };
    }

    // Try to get persisted cache (e.g., localStorage)
    const persistedEntry = getCacheEntry<T>(queryKey);
    if (persistedEntry) {
      const cacheAge = Date.now() - persistedEntry.metadata.timestamp;
      const isStale = cacheAge > maxAge;

      // Rehydrate in-memory cache for quicker subsequent reads
      this.cacheManager.set(cacheKey, persistedEntry.data, {
        priority,
        maxAge,
      });

      return {
        data: persistedEntry.data,
        isFromCache: true,
        cacheAge,
        isStale,
        lastUpdated: persistedEntry.metadata.timestamp,
        source: 'cache',
      };
    }

    // Try React Query cache as last resort
    const queryCache = this.queryClient.getQueryData(queryKey);
    if (queryCache) {
      const queryState = this.queryClient.getQueryState(queryKey);
      const cacheAge = queryState?.dataUpdatedAt ? Date.now() - queryState.dataUpdatedAt : Infinity;
      const isStale = cacheAge > maxAge;

      return {
        data: queryCache as T,
        isFromCache: true,
        cacheAge,
        isStale,
        lastUpdated: queryState?.dataUpdatedAt || 0,
        source: 'cache',
      };
    }

    // Return fallback value if no cached data available
    return {
      data: fallbackValue,
      isFromCache: false,
      cacheAge: Infinity,
      isStale: true,
      lastUpdated: 0,
      source: 'fallback',
    };
  }

  public async prefetchData<T>(
    queryKey: string[],
    fetcher: () => Promise<T>,
    options: FallbackDataOptions = {}
  ): Promise<void> {
    const { priority = 'low', maxAge = 30 * 60 * 1000 } = options;

    try {
      const data = await fetcher();
      
      // Store in both caches
      this.cacheManager.set(queryKey.join(':'), data, {
        priority,
        maxAge,
      });

      this.queryClient.setQueryData(queryKey, data);
      saveToCache(queryKey, data);
      
      console.debug(`Prefetched data for ${queryKey.join(':')}`);
    } catch (error) {
      console.warn(`Failed to prefetch data for ${queryKey.join(':')}:`, error);
    }
  }

  public warmCache(criticalQueries: Array<{
    queryKey: string[];
    fetcher: () => Promise<any>;
    options?: FallbackDataOptions;
  }>): Promise<void[]> {
    console.info(`Warming cache for ${criticalQueries.length} critical queries`);
    
    return Promise.allSettled(
      criticalQueries.map(({ queryKey, fetcher, options }) =>
        this.prefetchData(queryKey, fetcher, { ...options, priority: 'high' })
      )
    ).then(results => {
      const successful = results.filter(r => r.status === 'fulfilled').length;
      console.info(`Cache warming completed: ${successful}/${results.length} successful`);
      return results.map(() => undefined);
    });
  }

  public getCacheStats(): {
    totalEntries: number;
    cacheHitRate: number;
    averageAge: number;
    staleEntries: number;
  } {
    return this.cacheManager.getStats();
  }

  public pruneCacheByUsage(keepTopN: number = 100): number {
    return this.cacheManager.pruneByUsage(keepTopN);
  }

  public pruneCacheByAge(maxAge: number = 24 * 60 * 60 * 1000): number {
    return this.cacheManager.pruneByAge(maxAge);
  }

  private extractEndpointFromQueryKey(queryKey: string[]): string {
    // Extract endpoint from query key for failure tracking
    if (queryKey.length > 0) {
      const firstKey = queryKey[0];
      if (firstKey.includes('campaigns')) return '/api/campaigns';
      if (firstKey.includes('tracking')) return '/api/tracking';
      if (firstKey.includes('analytics')) return '/api/analytics';
    }
    return '/api/unknown';
  }
}

// Hook for using fallback data service
export const useFallbackData = () => {
  const queryClient = useQueryClient();
  const [fallbackService] = useState(() => new FallbackDataService(queryClient));
  
  return fallbackService;
};
